/**
 * useOfflineSync.js
 *
 * Central hook that wires together:
 *   • SQLite PendingOrders queue (insert / read / retry)
 *   • SyncManager (background sync engine)
 *   • Network status
 *
 * Usage:
 *   const {
 *     isOnline,
 *     pendingCount, failedCount, syncingCount,
 *     pendingOrders,
 *     submitOrderOffline,
 *     retryAll,
 *     retryOne,
 *     removeOrder,
 *     refreshCounts,
 *   } = useOfflineSync();
 */

import {useCallback, useEffect, useRef, useState} from 'react';
import useNetworkStatus from './useNetworkStatus';
import {
  countOrdersByStatus,
  deleteOrder,
  getOrdersByStatus,
  insertPendingOrder,
  resetAllFailedOrders,
  resetOrderToPending,
} from '../database/OfflineSyncDatabase';
import {
  onSyncUpdate,
  processQueue,
  registerBackgroundSync,
  startNetworkListener,
  stopNetworkListener,
} from '../SyncManager';
import {generateObjectId} from '../objectId';
import {generateHappyId} from '../happyId';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Observables from '@happy-gastro/global-observables';
import {normalizeOrderType} from '../orderVatResolver';

const DEFAULT_SYNC_STATUS = {
  apiToLocal: {
    state: 'idle',
    lastAttemptAt: null,
    lastSuccessAt: null,
    reason: null,
    reasonType: null,
    httpStatus: null,
    fetchedCount: 0,
  },
  localToApi: {
    state: 'idle',
    lastAttemptAt: null,
    lastSuccessAt: null,
    reason: null,
    reasonType: null,
    httpStatus: null,
    orderId: null,
    retryCount: 0,
    nextRetryInMs: null,
  },
};

const useOfflineSync = () => {
  const { isConnected, isInternetReachable } = useNetworkStatus();
  const isOnline = isConnected && isInternetReachable !== false;

  const [pendingCount, setPendingCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [syncingCount, setSyncingCount] = useState(0);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [syncStatus, setSyncStatus] = useState(DEFAULT_SYNC_STATUS);

  const mountedRef = useRef(true);

  // ── Refresh counters ────────────────────────────────────────────────────
  const refreshCounts = useCallback(async () => {
    try {
      const [p, f, s] = await Promise.all([
        countOrdersByStatus('Pending'),
        countOrdersByStatus('Failed'),
        countOrdersByStatus('Syncing'),
      ]);
      if (mountedRef.current) {
        setPendingCount(p);
        setFailedCount(f);
        setSyncingCount(s);
      }
    } catch (e) {
      console.warn('[useOfflineSync] refreshCounts error', e.message);
    }
  }, []);

  // ── Load full list of pending + failed orders (for the modal) ──────────
  const loadPendingOrders = useCallback(async () => {
    try {
      const orders = await getOrdersByStatus(['Pending', 'Syncing', 'Failed']);
      if (mountedRef.current) {
        setPendingOrders(orders);
      }
    } catch (e) {
      console.warn('[useOfflineSync] loadPendingOrders error', e.message);
    }
  }, []);

  // ── Bootstrap ─────────────────────────────────────────────────────────
  useEffect(() => {
    mountedRef.current = true;

    // Start listening for network changes → auto-sync
    startNetworkListener();

    // Register expo-task-manager background sync
    registerBackgroundSync();

    // Subscribe to sync-cycle updates from SyncManager
    const unsubscribe = onSyncUpdate(({ pendingCount: p, failedCount: f, syncingCount: s }) => {
      if (mountedRef.current) {
        setPendingCount(p);
        setFailedCount(f);
        setSyncingCount(s);
      }

      loadPendingOrders();
    });

    Observables.register('offlineSync:status', (payload = {}) => {
      const channel = payload?.channel;
      if (!channel || (channel !== 'apiToLocal' && channel !== 'localToApi')) {
        return;
      }

      setSyncStatus((previous) => {
        const prevChannel = previous[channel] || {};
        const timestamp = payload.timestamp || new Date().toISOString();
        const nextState = payload.state || prevChannel.state || 'idle';

        return {
          ...previous,
          [channel]: {
            ...prevChannel,
            ...payload,
            state: nextState,
            lastAttemptAt: timestamp,
            lastSuccessAt: nextState === 'success' ? timestamp : prevChannel.lastSuccessAt || null,
          },
        };
      });

      if (channel === 'localToApi') {
        // Keep status screen list fresh when sync enters waiting/retrying states.
        refreshCounts();
        loadPendingOrders();
      }
    });

    // Initial load
    refreshCounts();
    loadPendingOrders();

    // Periodic safety-net refresh to catch any stale counts. Real updates are
    // already event-driven (onSyncUpdate above + the 'offlineSync:status'
    // Observable), so this blind poll is only a backstop — 60s (was 10s) cuts
    // the idle-time SQLite count/list queries 6× on every mode without delaying
    // any genuine sync-triggered refresh.
    const refreshInterval = globalThis.setInterval(() => {
      if (mountedRef.current) {
        refreshCounts();
        loadPendingOrders();
      }
    }, 60000);

    return () => {
      mountedRef.current = false;
      stopNetworkListener();
      unsubscribe();
      globalThis.clearInterval(refreshInterval);
      if (typeof Observables.remove === 'function') {
        Observables.remove('offlineSync:status');
      }
    };
  }, [refreshCounts, loadPendingOrders]);

  // ── When connectivity returns, kick sync and refresh ──────────────────
  // Note: SyncManager has its own network listener too, but processQueue()
  // is safe to call concurrently (it deduplicates via a shared promise).
  useEffect(() => {
    if (isOnline) {
      // Small delay to let SyncManager's own listener fire first and
      // avoid redundant NetInfo.fetch() calls.
      const timer = globalThis.setTimeout(() => {
        processQueue().then(() => {
          refreshCounts();
          loadPendingOrders();
        });
      }, 500);
      return () => globalThis.clearTimeout(timer);
    }
  }, [isOnline, refreshCounts, loadPendingOrders]);

  // ── Submit a new order to the offline queue ────────────────────────────
  /**
   * Persist an order locally and immediately kick sync.
   * Returns a mock order response that the UI can use for receipt printing.
   */
  const submitOrderOffline = useCallback(async (orderData, orderId) => {
    const id = orderId || generateObjectId();
    const orderCode = generateHappyId();

    // Stamp the stable orderCode into the order data BEFORE insertion so that
    // the SQLite record and the mock response use the exact same code.
    const enrichedOrderData = {
      ...orderData,
      _stableOrderCode: orderData._stableOrderCode || orderCode,
      _stableGeneratedId: orderData._stableGeneratedId || id,
    };

    await insertPendingOrder(enrichedOrderData, id);

    // Build a local-only response for the UI / receipt printer
    const mockResponse = {
      _id: id,
      orderCode: enrichedOrderData._stableOrderCode,
      items: [...(orderData.cartItems || []), ...(orderData.alreadySentCartItems || [])],
      orderSum: orderData.cartTotal,
      paymentType: orderData.paymentType,
      paymentTypes: orderData.paymentTypes,
      status: 'pending_sync',
      createdAt: new Date().toISOString(),
      isOfflineOrder: true,
      discounts: orderData.discountsForWholeCart,
      serviceFee: orderData.serviceFeeAmount,
      orderType: (orderData.isSavedForLater || orderData.selectedItemsToPay?.length > 0 || orderData.currentTable)
        ? 'dineIn'
        : normalizeOrderType(orderData.userData?.orderType, 'takeAway'),
      tableId: orderData.currentTable?._id,
      tableName: orderData.currentTable?.name,
    };

    // Persist for other parts of the app that read from AsyncStorage
    await AsyncStorage.setItem('lastOrderResponse', JSON.stringify(mockResponse));
    await AsyncStorage.setItem('orderStored', 'true');

    // Kick sync engine (non-blocking)
    processQueue().catch(() => {});
    refreshCounts();
    loadPendingOrders();

    return mockResponse;
  }, [refreshCounts, loadPendingOrders]);

  // ── Retry helpers ──────────────────────────────────────────────────────
  const retryAll = useCallback(async () => {
    await resetAllFailedOrders();
    await Promise.all((pendingOrders || []).map((queuedOrder) => resetOrderToPending(queuedOrder.id)));
    await refreshCounts();
    await loadPendingOrders();
    processQueue().catch(() => {});
  }, [pendingOrders, refreshCounts, loadPendingOrders]);

  const retryOne = useCallback(async (id) => {
    await resetOrderToPending(id);
    await refreshCounts();
    await loadPendingOrders();
    processQueue().catch(() => {});
  }, [refreshCounts, loadPendingOrders]);

  const removeOrder = useCallback(async (id) => {
    await deleteOrder(id);
    await refreshCounts();
    await loadPendingOrders();
  }, [refreshCounts, loadPendingOrders]);

  const retryApiToLocalSync = useCallback(async () => {
    setSyncStatus((previous) => ({
      ...previous,
      apiToLocal: {
        ...previous.apiToLocal,
        state: 'syncing',
        lastAttemptAt: new Date().toISOString(),
        reason: null,
        reasonType: null,
        httpStatus: null,
      },
    }));

    try {
      Observables.call('reloadOrders');
    } catch (_) {
      // noop
    }
  }, []);

  return {
    // Network
    isOnline,

    // Counts (for badge / indicators)
    pendingCount,
    failedCount,
    syncingCount,
    totalUnsyncedCount: pendingCount + failedCount + syncingCount,
    syncStatus,

    // Full list (for modal)
    pendingOrders,
    loadPendingOrders,

    // Actions
    submitOrderOffline,
    retryAll,
    retryOne,
    removeOrder,
    refreshCounts,
    retryApiToLocalSync,
  };
};

export default useOfflineSync;

